import { useQuery } from '@tanstack/react-query';
import { supabase } from '../services/supabase';

export interface Post {
  id: string;
  title?: string;
  content: string;
  created_at: string;
  author_id: string;
  author_name?: string;
  status?: string;
  is_flagged?: boolean;
  is_pinned?: boolean;
  images?: string[];
  video_link?: string;
  video_url?: string;
  wall_comments?: { count: number }[];
  post_likes?: { count: number }[];
  type?: string;
}

export type PostFilter = 'all' | 'flagged' | 'hidden';

export const usePosts = (
  filter: PostFilter = 'all',
  options?: { type?: string; status?: string }
) => {
  return useQuery<Post[]>({
    queryKey: ['posts', filter, options?.type ?? '', options?.status ?? ''],
    queryFn: async () => {
      let query = supabase
        .from('wall_posts')
        .select('*, wall_comments(count), post_likes(count)')
        .order('created_at', { ascending: false })
        .limit(100);

      if (options?.type) {
        query = query.eq('type', options.type);
      }
      if (options?.status) {
        query = query.eq('status', options.status);
      }

      if (filter === 'flagged') {
        query = query.eq('is_flagged', true);
      } else if (filter === 'hidden') {
        query = query.eq('status', 'hidden');
      }

      const { data: posts, error } = await query;
      if (error) throw error;

      // Fetch author names separately (no FK defined)
      const authorIds: string[] = [];
      (posts ?? []).forEach((p: any) => {
        if (p.author_id && !authorIds.includes(p.author_id)) authorIds.push(p.author_id);
      });
      let usersMap: Record<string, string> = {};
      if (authorIds.length) {
        const { data: users } = await supabase
          .from('users')
          .select('id, full_name')
          .in('id', authorIds);
        users?.forEach((u) => {
          usersMap[u.id as string] = (u.full_name as string) || '';
        });
      }

      const enriched = (posts ?? []).map((p: any) => ({
        ...p,
        author_name: usersMap[p.author_id] ?? p.author_id,
      }));

      return enriched as Post[];
    },
  });
};
